import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Typography } from '@altermaind/web/ui';
import { RenderLocalesWithComponents, useDrawer } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { usePaginatedTable } from '@/_shared/components/Table';
import { TableHeader } from '@/_shared/components/TableHeader';
import { Claims } from '@/_shared/constants/claims';
import { getDocLink } from '@/_shared/constants/linksDoc';
import { useDialog } from '@/_shared/hooks/useDialog';
import { User } from '@/organization/state/types';
import { AddDelegate } from './AddDelegate';
import { FilterDelegates } from './FilterDelegates';
import { RemoveDelegate } from './RemoveDelegate';
import { ResendInviteDelegate } from './ResendInviteDelegate';
import { TableDelegates } from './TableDelegates';

type Props = {
  hrDelegates: User[];
  selectedDelegate?: User | undefined;
  setSelectedDelegate: (delegate: User | undefined) => void;
};

export const DelegatesList: FC<Props> = ({
  hrDelegates,
  selectedDelegate,
  setSelectedDelegate,
}) => {
  const t = useTranslations('settings.tabs.delegates.addDelegate.emptyState');
  const tDelegates = useTranslations('settings.tabs.delegates');
  const { isOpen: isOpenDrawer, close, ...drawerProps } = useDrawer();
  const [filters, setFilters] = useState<{ search?: string; status?: string[] }>({});
  const {
    openDialog: openDialogRemoveDelegate,
    isOpen: isOpenRemove,
    closeDialog: closeDialogRemove,
  } = useDialog('removeDelegate');
  const {
    openDialog: openDialogResendInviteDelegate,
    isOpen: isOpenResendInvite,
    closeDialog: closeDialogResendInvite,
  } = useDialog('resendInviteDelegate');
  const {
    isOpen: isOpenAdd,
    openDialog: openDialogAddDelegate,
    closeDialog: closeDialogAdd,
  } = useDialog('addDelegate');
  const { openDialog: openContacts } = useDialog('contacts');

  const [internalDelegates, setInternalDelegates] = useState<User[]>(hrDelegates);

  const paginedProps = usePaginatedTable({
    rowData: internalDelegates,
    pageSize: 1000,
    totalItems: internalDelegates.length,
  });

  const onSearchCallback = useCallback(
    (value: string) => {
      setFilters(prev => ({ ...prev, search: value }));
    },
    [setFilters],
  );

  const goToDocs = useCallback(() => {
    window.open(getDocLink('ADMIN_AND_DELEGATES_REVIEW_AND_CONFIRM'), '_blank');
  }, []);

  const goToContact = useCallback(() => {
    openContacts({});
  }, [openContacts]);

  useEffect(() => {
    if (filters.search) {
      setInternalDelegates(
        hrDelegates.filter(delegate =>
          delegate.email.toLowerCase().includes(filters.search!.toLowerCase()),
        ),
      );
    } else {
      setInternalDelegates(hrDelegates);
    }

    if (filters.status && filters.status.length > 0) {
      setInternalDelegates(prev =>
        prev.filter(delegate => filters.status!.includes(delegate.status)),
      );
    }
  }, [filters, hrDelegates]);

  return (
    <Card overflow='visible' mt='xs' display='flex' flexDirection='column'>
      {hrDelegates.length === 0 && (
        <>
          <Typography textAlign='center' variant='h3' mb='xs' flex='1'>
            {t('title')}
          </Typography>
          <Box maxWidth='80%' mx='auto' textAlign='center'>
            <Typography variant='p2/light' mb='s' textAlign='center'>
              {t('description')}
            </Typography>
            <AlertBadge
              type='transparent'
              iconName='lightBulb'
              label={
                <Typography variant='p2/light'>
                  <RenderLocalesWithComponents
                    text={t('alert', {
                      downloadFile: '!{DOCS}!',
                    })}
                    placeholders={{
                      '!{DOCS}!': (
                        <Typography
                          as='span'
                          fontWeight='500'
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                          }}
                          onClick={goToDocs}
                        >
                          {t('labelDocs')}
                        </Typography>
                      ),
                      '!{CONTACT}!': (
                        <Typography
                          as='span'
                          fontWeight='500'
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                          }}
                          onClick={goToContact}
                        >
                          {t('labelContact')}
                        </Typography>
                      ),
                    }}
                  />
                </Typography>
              }
            />
            <Button mx='auto' mt='s' variant='positive' onClick={openDialogAddDelegate}>
              {t('button')}
            </Button>
          </Box>
        </>
      )}
      {hrDelegates.length > 0 && (
        <>
          <TableHeader
            onSearchCallback={onSearchCallback}
            title={t('title')}
            searchLabel={tDelegates('searchDelegates')}
            buttonAddOnClick={openDialogAddDelegate}
            onAddClaimKey={Claims.ORGANIZATION_WRITE}
            filtersComponent={<FilterDelegates setFilters={setFilters} filters={filters} />}
          />
          <TableDelegates
            setSelectedDelegate={setSelectedDelegate}
            selectedDelegate={selectedDelegate}
            paginedProps={paginedProps}
            drawer={{ ...drawerProps, isOpen: isOpenDrawer, close, instances: [] }}
            openDialogRemoveDelegate={openDialogRemoveDelegate}
            openDialogResendInviteDelegate={openDialogResendInviteDelegate}
          />
          <ResendInviteDelegate
            isOpen={isOpenResendInvite}
            closeDialog={closeDialogResendInvite}
            userId={selectedDelegate?.id || ''}
            fullName={`${selectedDelegate?.firstName || ''} ${selectedDelegate?.lastName || ''}`}
          />
          <RemoveDelegate
            isOpen={isOpenRemove}
            closeDialog={closeDialogRemove}
            userId={selectedDelegate?.id || ''}
          />
        </>
      )}
      <AddDelegate isOpen={isOpenAdd} closeDialog={closeDialogAdd} />
    </Card>
  );
};
